import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
} from 'react';

import { Flex } from '@banhala/ui-kit';
import {
  AtomicFooterType,
  AtomicHeaderType,
  AtomicItemListType,
  type IScreensComponent,
} from '../../../api';
import type { ILogging } from '../../../api/types';
// Header - 직접 import (barrel 순환 참조 방지)
import { HeadlineSmallOneLine } from '../Header/HeadlineSmallOneLine';
import { HeadlineSmallOneLineWithText } from '../Header/HeadlineSmallOneLineWithText';
import { HeadlineSmallOneLineWithTextCompact } from '../Header/HeadlineSmallOneLineWithTextCompact';
import { HeadlineXsmallOneLine } from '../Header/HeadlineXsmallOneLine';
import { HeadlineOnlyText } from '../Header/HeadlineOnlyText';
import { HeadlineSmallTwoLineWithTimer } from '../Header/HeadlineSmallTwoLineWithTimer';
import { HeadlineReloadOneLine } from '../Header/HeadlineReloadOneLine';
import { HeadlineLargeTwoLine } from '../Header/HeadlineLargeTwoLine';
import { HeadlineSmallOneLineCompact } from '../Header/HeadlineSmallOneLineCompact';
import { HeadlineOverlineTimer } from '../Header/HeadlineOverlineTimer';
import { HeadlineSelectableOneLine } from '../Header/HeadlineSelectableOneLine';
import { HeadlineSmallOneLineToggle } from '../Header/HeadlineSmallOneLineToggle';
import { HeadlineSmallOneLineWithTimer } from '../Header/HeadlineSmallOneLineWithTimer';
import { HeadlineSmallTwoLine } from '../Header/HeadlineSmallTwoLine';
import { HeadlineXsmallOneLineCompact } from '../Header/HeadlineXsmallOneLineCompact';
import { HeadlineXsmallOneLineSegments } from '../Header/HeadlineXsmallOneLineSegments';
import { HeadlineXsmallOneLineToggle } from '../Header/HeadlineXsmallOneLineToggle';

// ItemList - 직접 import (barrel 순환 참조 방지)
import { ProfileInfo } from '../ItemList/ProfileInfo';
import { Divider } from '../ItemList/Divider';
import { GoodsCarousel } from '../ItemList/GoodsCarousel';
import { FilterList } from '../ItemList/FilterList';
import { TwoColGoodsList } from '../ItemList/TwoColGoodsList';
import { TwoColCardList } from '../ItemList/TwoColCardList';
import { ThreeColGoodsList } from '../ItemList/ThreeColGoodsList';
import { ThreeColCardList } from '../ItemList/ThreeColCardList';
import { TwoLineMessage } from '../ItemList/TwoLineMessage';
import { Proxy } from '../ItemList/Proxy';
import { Empty } from '../ItemList/Empty';
import { NotificationGuide } from '../ItemList/NotificationGuide';
import { CompanyInfo } from '../ItemList/CompanyInfo';
import { SearchBar } from '../ItemList/SearchBar';
import { NeonSignBoard } from '../ItemList/NeonSignBoard';
import { Summary } from '../ItemList/Summary';
import { HorizontalChipList } from '../ItemList/HorizontalChipList';
import {
  FourColIconList,
  FiveColIconList,
  ScrollableIconList,
} from '../ItemList/IconList';
import { MarketBadgeList } from '../ItemList/MarketBadgeList';
import { MarketSmallBadgeList } from '../ItemList/MarketSmallBadgeList';
import { LiveProfileBadgeList } from '../ItemList/LiveProfileBadgeList';
import { StoreList } from '../ItemList/StoreList';
import {
  TwoImageMarketCarousel,
  ThreeImageMarketCarousel,
} from '../ItemList/MarketCarousel';
import { RollingBannerList } from '../ItemList/RollingBannerList';
import { BannerCarousel } from '../ItemList/BannerCarousel';
import { DescriptionCardCarousel } from '../ItemList/DescriptionCardCarousel';
import { VerticalBannerList } from '../ItemList/VerticalBannerList';
import { TwoColOutfitList } from '../ItemList/TwoColOutfitList';
import { RelatedEvent } from '../ItemList/RelatedEvent';
import { MarketBrandingAds } from '../ItemList/MarketBrandingAds';
import { MultiCheckboxFilter } from '../ItemList/MultiCheckboxFilter';
import { TodayRollingBanner } from '../ItemList/TodayRollingBanner';
import { TodayShortBanner } from '../ItemList/TodayShortBanner';
import { CheckboxFilterList } from '../ItemList/CheckboxFilterList';
import { DownloadableCoupons } from '../ItemList/DownloadableCoupons';
import { DownloadedCoupons } from '../ItemList/DownloadedCoupons';
import { EventGrid } from '../ItemList/EventGrid';
import { ExpandableCategoryList } from '../ItemList/ExpandableCategoryList';
import { FlipDescriptionCard } from '../ItemList/FlipDescriptionCard';
import { FrequentlyVisitedMarketList } from '../ItemList/FrequentlyVisitedMarketList';
import { GoodsCarouselCalendar } from '../ItemList/GoodsCarouselCalendar';
import { HorizontalImageChipList } from '../ItemList/HorizontalImageChipList';
import { InputNonEntryMarket } from '../ItemList/InputNonEntryMarket';
import { OneCheckboxFilter } from '../ItemList/OneCheckboxFilter';
import { OutfitCarousel } from '../ItemList/OutfitCarousel';
import { PromotionShortBanner } from '../ItemList/PromotionShortBanner';
import { SortingGoodsList } from '../ItemList/SortingGoodsList';
import { TasteOnboardingCard } from '../ItemList/TasteOnboardingCard';
import { ThreeColFilter } from '../ItemList/ThreeColFilter';
import { TwoColBrandGoodsList } from '../ItemList/TwoColBrandGoodsList';
import { TwoColFeaturedShortcutList } from '../ItemList/TwoColFeaturedShortcutList';

// Footer - 직접 import
import { FooterMore } from '../Footer/FooterMore';
import { FooterMoreSmall } from '../Footer/FooterMoreSmall';
import { useAtomicState } from '../state';
import { useScreenComponentQuery } from './useScreenComponentQuery';

export const HeaderComponent = {
  [AtomicHeaderType.HeadlineSmallOneLine]: HeadlineSmallOneLine,
  [AtomicHeaderType.HeadlineXsmallOneLine]: HeadlineXsmallOneLine,
  [AtomicHeaderType.HeadlineSmallOneLineWithText]: HeadlineSmallOneLineWithText,
  [AtomicHeaderType.HeadlineSmallOneLineWithTextCompact]:
    HeadlineSmallOneLineWithTextCompact,
  [AtomicHeaderType.HeadlineOnlyText]: HeadlineOnlyText,
  [AtomicHeaderType.HeadlineSmallTwoLineWithTimer]:
    HeadlineSmallTwoLineWithTimer,
  [AtomicHeaderType.HeadlineReloadOneLine]: HeadlineReloadOneLine,
  [AtomicHeaderType.HeadlineLargeTwoLine]: HeadlineLargeTwoLine,
  [AtomicHeaderType.HeadlineSmallOneLineCompact]: HeadlineSmallOneLineCompact,
  [AtomicHeaderType.HeadlineOverlineTimer]: HeadlineOverlineTimer,
  [AtomicHeaderType.HeadlineSelectableOneLine]: HeadlineSelectableOneLine,
  [AtomicHeaderType.HeadlineSmallOneLineToggle]: HeadlineSmallOneLineToggle,
  [AtomicHeaderType.HeadlineSmallOneLineWithTimer]:
    HeadlineSmallOneLineWithTimer,
  [AtomicHeaderType.HeadlineSmallTwoLine]: HeadlineSmallTwoLine,
  [AtomicHeaderType.HeadlineXsmallOneLineCompact]: HeadlineXsmallOneLineCompact,
  [AtomicHeaderType.HeadlineXsmallOneLineSegments]:
    HeadlineXsmallOneLineSegments,
  [AtomicHeaderType.HeadlineXsmallOneLineToggle]: HeadlineXsmallOneLineToggle,
};

export const ItemListComponent = {
  [AtomicItemListType.PROFILE_INFO]: ProfileInfo,
  [AtomicItemListType.Divider]: Divider,
  [AtomicItemListType.GoodsCarousel]: GoodsCarousel,
  [AtomicItemListType.FilterList]: FilterList,
  [AtomicItemListType.TwoColGoodsList]: TwoColGoodsList,
  [AtomicItemListType.TwoColCardList]: TwoColCardList,
  [AtomicItemListType.ThreeColGoodsList]: ThreeColGoodsList,
  [AtomicItemListType.ThreeColCardList]: ThreeColCardList,
  [AtomicItemListType.TwoLineMessage]: TwoLineMessage,
  [AtomicItemListType.Proxy]: Proxy,
  [AtomicItemListType.Empty]: Empty,
  [AtomicItemListType.NotificationGuide]: NotificationGuide,
  [AtomicItemListType.CompanyInfo]: CompanyInfo,
  [AtomicItemListType.SearchBar]: SearchBar,
  [AtomicItemListType.NeonSignBoard]: NeonSignBoard,
  [AtomicItemListType.Summary]: Summary,
  // Phase 2: Medium-Easy (단순 리스트형)
  [AtomicItemListType.HorizontalChipList]: HorizontalChipList,
  [AtomicItemListType.FourColIconList]: FourColIconList,
  [AtomicItemListType.FiveColIconList]: FiveColIconList,
  [AtomicItemListType.ScrollableIconList]: ScrollableIconList,
  [AtomicItemListType.MarketBadgeList]: MarketBadgeList,
  [AtomicItemListType.MarketSmallBadgeList]: MarketSmallBadgeList,
  [AtomicItemListType.LiveProfileBadgeList]: LiveProfileBadgeList,
  [AtomicItemListType.StoreList]: StoreList,
  [AtomicItemListType.TwoImageMarketCarousel]: TwoImageMarketCarousel,
  [AtomicItemListType.ThreeImageMarketCarousel]: ThreeImageMarketCarousel,
  [AtomicItemListType.RollingBannerList]: RollingBannerList,
  [AtomicItemListType.BannerCarousel]: BannerCarousel,
  [AtomicItemListType.DescriptionCardCarousel]: DescriptionCardCarousel,
  [AtomicItemListType.VerticalBannerList]: VerticalBannerList,
  [AtomicItemListType.TwoColOutfitList]: TwoColOutfitList,
  [AtomicItemListType.RelatedEvent]: RelatedEvent,
  [AtomicItemListType.MarketBrandingAds]: MarketBrandingAds,
  [AtomicItemListType.MultiCheckboxFilter]: MultiCheckboxFilter,
  [AtomicItemListType.TodayRollingBanner]: TodayRollingBanner,
  [AtomicItemListType.TodayShortBanner]: TodayShortBanner,
  [AtomicItemListType.CheckboxFilterList]: CheckboxFilterList,
  [AtomicItemListType.DownloadableCoupons]: DownloadableCoupons,
  [AtomicItemListType.DownloadedCoupons]: DownloadedCoupons,
  [AtomicItemListType.EventGrid]: EventGrid,
  [AtomicItemListType.ExpandableCategoryList]: ExpandableCategoryList,
  [AtomicItemListType.FlipDescriptionCard]: FlipDescriptionCard,
  [AtomicItemListType.FrequentlyVisitedMarketList]: FrequentlyVisitedMarketList,
  [AtomicItemListType.GoodsCarouselCalendar]: GoodsCarouselCalendar,
  [AtomicItemListType.HorizontalImageChipList]: HorizontalImageChipList,
  [AtomicItemListType.InputNonEntryMarket]: InputNonEntryMarket,
  [AtomicItemListType.OneCheckboxFilter]: OneCheckboxFilter,
  [AtomicItemListType.OutfitCarousel]: OutfitCarousel,
  [AtomicItemListType.PromotionShortBanner]: PromotionShortBanner,
  [AtomicItemListType.SortingGoodsList]: SortingGoodsList,
  [AtomicItemListType.TasteOnboardingCard]: TasteOnboardingCard,
  [AtomicItemListType.ThreeColFilter]: ThreeColFilter,
  [AtomicItemListType.TwoColBrandGoodsList]: TwoColBrandGoodsList,
  [AtomicItemListType.TwoColFeaturedShortcutList]: TwoColFeaturedShortcutList,
};

export const FooterComponent = {
  [AtomicFooterType.More]: FooterMore,
  [AtomicFooterType.MoreSmall]: FooterMoreSmall,
};

export type AtomicComponentProps = {
  component: IScreensComponent;
  index: number;
  likeMap: Record<string, boolean>;
  setLikeMap: Dispatch<SetStateAction<Record<string, boolean>>>;
  bookmarkMap: Record<string, boolean>;
  setBookmarkMap: Dispatch<SetStateAction<Record<string, boolean>>>;
  scrollToFilterList?: VoidFunction;
  onReload?: () => void;
  onReloadWithParams?: (queryParams: Record<string, string>) => void;
  screen?: string;
  screenName?: string;
  screenLogging?: ILogging;
  logDisabled?: boolean;
  /** 부모 리스트의 onViewableItemsChanged로 판정된 뷰포트 가시성 (배너 autoScroll gate용) */
  isVisible?: boolean;
};

export const AtomicComponent = ({
  component,
  likeMap,
  setLikeMap,
  bookmarkMap,
  setBookmarkMap,
  scrollToFilterList,
  onReload,
  screen,
  screenName,
  screenLogging,
  logDisabled,
  isVisible,
  onReloadWithParams,
}: AtomicComponentProps) => {
  const Header = component.type.header
    ? HeaderComponent[component.type.header]
    : null;

  const Footer = component.type.footer
    ? FooterComponent[component.type.footer]
    : null;

  const ItemList = component.type.itemList
    ? ItemListComponent[component.type.itemList]
    : null;

  const {
    params: atomicParams,
    setParam,
    initParams,
  } = useAtomicState(screenName ?? '', component.componentId ?? 0);

  const { data: queryData, isFetching } = useScreenComponentQuery({
    screenName,
    componentId: component.componentId,
    screenLogging,
    extraParams: atomicParams,
  });

  const effectiveEntity = useMemo(
    () => queryData?.entity ?? component.entity,
    [queryData, component.entity],
  );

  const handleChipPress = useCallback(
    (qName: string, queryValue: string) => {
      setParam(qName, queryValue);
    },
    [setParam],
  );

  return (
    <Flex>
      {Header && (
        <Flex>
          <Header
            screen={screen}
            header={effectiveEntity.header ?? component.entity.header}
            onReload={onReload}
          />
        </Flex>
      )}
      {ItemList && (effectiveEntity.itemList ?? component.entity.itemList) && (
        <Flex>
          <ItemList
            screen={screen}
            screenName={screenName}
            screenLogging={screenLogging}
            componentId={component.componentId}
            entity={(effectiveEntity.itemList ?? component.entity.itemList)!}
            body={effectiveEntity.body ?? component.entity.body}
            logging={component.logging}
            scrollToFilterList={
              scrollToFilterList ? scrollToFilterList : () => {}
            }
            likeMap={likeMap}
            logDisabled={logDisabled}
            isVisible={isVisible}
            setLikeMap={setLikeMap}
            bookmarkMap={bookmarkMap}
            setBookmarkMap={setBookmarkMap}
            onReloadWithParams={onReloadWithParams}
            isFetching={isFetching}
            atomicParams={atomicParams}
            onChipPress={handleChipPress}
            initParams={initParams}
          />
        </Flex>
      )}
      {Footer && (
        <Flex>
          <Footer
            screen={screen}
            footer={effectiveEntity.footer ?? component.entity.footer}
          />
        </Flex>
      )}
    </Flex>
  );
};
